/**
 * MCG Commercial IPTV Uploader Page
 * 
 * Premium themed upload form for commercial IPTV content.
 */

import React, { useState, useCallback, useRef } from "react";
import { useAuthUser } from "../../../hooks/useAuthUser";
import UserDetailsFields from "../../../components/uploaders/UserDetailsFields";
import { useVFContext } from "../../../context/VFContext";
import { useToast } from "../../../components/ui/UploaderToast";
import UploaderSection from "../../../components/uploaders/UploaderSection";
import UploadSuccessModal from "../../../components/modal/UploadSuccessModal";
import {
  IPTV_PLACEMENTS,
  IPTV_PLACEMENT_GROUPS,
  UPLOADER_SPECS,
  METADATA_WEBHOOK,
} from "../../../config/mcgCommercialIptvSpecs";
import { generateSessionId, generateCrockfordTimeId } from "../../../services/idGenerator";

// ============================================================
// STYLES - Premium Theme
// ============================================================
const styles = {
  page: { minHeight: "100vh", height: "100%", backgroundColor: "#f7f7f5", fontFamily: "'Space Grotesk', -apple-system, sans-serif" },
  header: { backgroundColor: "#111114", padding: "18px 32px", display: "flex", alignItems: "center", justifyContent: "space-between" },
  headerTitle: { fontSize: 18, fontWeight: 600, color: "#fff", margin: 0 },
  headerSub: { fontSize: 13, color: "rgba(255,255,255,0.5)", marginTop: 2 },
  headerBadges: { display: "flex", alignItems: "center", gap: 8 },
  venueBadge: { backgroundColor: "rgba(47, 214, 181, 0.15)", border: "1px solid rgba(47, 214, 181, 0.4)", color: "#2fd6b5", padding: "6px 14px", borderRadius: 6, fontSize: 12, fontWeight: 600 },
  commercialBadge: { backgroundColor: "rgba(251, 191, 36, 0.15)", border: "1px solid rgba(251, 191, 36, 0.4)", color: "#fbbf24", padding: "6px 14px", borderRadius: 6, fontSize: 12, fontWeight: 600 },
  accentLine: { height: 3, background: "linear-gradient(90deg, #2fd6b5, #84cc16)" },
  content: { padding: "32px 40px 48px", maxWidth: 1100, margin: "0 auto" },
  sectionLabel: { fontSize: 11, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 },
  typeSection: { marginBottom: 28 },
  typeRow: { display: "flex", flexWrap: "wrap", gap: 10 },
  typeBtn: { padding: "10px 18px", fontSize: 13, fontWeight: 500, color: "#555", backgroundColor: "#fff", border: "1px solid #ddd", borderRadius: 8, cursor: "pointer", transition: "all 0.15s ease", fontFamily: "inherit" },
  typeBtnHover: { borderColor: "#bbb", backgroundColor: "#fafafa", color: "#333" },
  typeBtnActive: { backgroundColor: "#111114", borderColor: "#111114", color: "#fff", fontWeight: 600 },
  uploadSection: { backgroundColor: "#fff", borderRadius: 14, border: "2px solid #2fd6b5", boxShadow: "0 0 0 4px rgba(47, 214, 181, 0.1), 0 4px 20px rgba(0, 0, 0, 0.06)", overflow: "hidden", marginBottom: 28 },
  uploadHeader: { backgroundColor: "#111114", padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" },
  uploadTitle: { fontSize: 14, fontWeight: 600, color: "#fff", margin: 0, display: "flex", alignItems: "center", gap: 8 },
  uploadTitleIcon: { color: "#2fd6b5" },
  uploadBody: { padding: 24 },
  notesSection: { marginBottom: 32 },
  textarea: { width: "100%", padding: "14px 16px", fontSize: 14, color: "#333", backgroundColor: "#fff", border: "1px solid #ddd", borderRadius: 10, resize: "vertical", minHeight: 80, fontFamily: "inherit", outline: "none", boxSizing: "border-box", transition: "border-color 0.15s ease, box-shadow 0.15s ease" },
  textareaFocus: { borderColor: "#2fd6b5", boxShadow: "0 0 0 3px rgba(47, 214, 181, 0.1)" },
  charCount: { fontSize: 11, color: "#999", textAlign: "right", marginTop: 6 },
  footer: { paddingTop: 24, borderTop: "1px solid #e5e5e3", display: "flex", justifyContent: "flex-end" },
  submitBtn: { padding: "12px 32px", fontSize: 15, fontWeight: 600, color: "#000", background: "linear-gradient(135deg, #2fd6b5 0%, #84cc16 100%)", border: "none", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 8, transition: "all 0.15s ease" },
  submitBtnHover: { transform: "translateY(-1px)", boxShadow: "0 6px 16px rgba(47, 214, 181, 0.35)" },
  submitBtnDisabled: { opacity: 0.4, cursor: "not-allowed", transform: "none", boxShadow: "none" },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 28 },
  field: {},
  label: { fontSize: 11, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8, display: "block" },
  input: { width: "100%", padding: "11px 14px", fontSize: 14, color: "#333", backgroundColor: "#fff", border: "1px solid #ddd", borderRadius: 8, fontFamily: "inherit", outline: "none", boxSizing: "border-box", transition: "border-color 0.15s ease, box-shadow 0.15s ease" },
  inputFocus: { borderColor: "#2fd6b5", boxShadow: "0 0 0 3px rgba(47, 214, 181, 0.1)" },
  placeholderBox: { backgroundColor: "#fafaf8", borderRadius: 10, border: "2px dashed #ddd", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 32, color: "#999", fontSize: 14, textAlign: "center", minHeight: 200 },
};

function TypeButton({ label, isActive, onClick, disabled }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button type="button" onClick={onClick} disabled={disabled} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ ...styles.typeBtn, ...(hovered && !isActive ? styles.typeBtnHover : {}), ...(isActive ? styles.typeBtnActive : {}), ...(disabled ? { opacity: 0.5, cursor: "not-allowed" } : {}) }}>
      {label}
    </button>
  );
}

const MAX_DESC = 250;

export function MCGCommercialIPTVPage() {
  const context = useVFContext();
  const { email: authEmail, name: authName } = useAuthUser();
  const { showError, ToastComponent } = useToast();

  const [sessionId, setSessionId] = useState(() => generateSessionId());
  const [submissionTimeId, setSubmissionTimeId] = useState(() => generateCrockfordTimeId());
  const [name, setName] = useState("");
  const [email, setEmail] = useState(authEmail || "");
  const [description, setDescription] = useState("");
  const [activePlacement, setActivePlacement] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [isSubmitHovered, setIsSubmitHovered] = useState(false);

  const [uploadResults, setUploadResults] = useState(() => {
    const results = {};
    Object.keys(UPLOADER_SPECS).forEach((key) => {
      const spec = UPLOADER_SPECS[key];
      results[key] = { contentGroup: spec.contentGroup, ledType: spec.ledType, assets: [] };
    });
    return results;
  });

  const activeUploads = useRef(0);
  const activeUploaderIds = activePlacement ? IPTV_PLACEMENT_GROUPS[activePlacement] || [] : [];

  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [lastUploadInfo, setLastUploadInfo] = useState(null);

  const handleUploadComplete = useCallback(({ uploadId, file }) => {
    activeUploads.current = Math.max(0, activeUploads.current - 1);
    setUploadResults((prev) => ({ ...prev, [uploadId]: { ...prev[uploadId], assets: [...(prev[uploadId]?.assets || []), file] } }));
  }, []);
  const handleUploadStart = useCallback(() => { activeUploads.current += 1; }, []);
  const handleUploadCancel = useCallback(({ uploadId, fileId }) => {
    activeUploads.current = Math.max(0, activeUploads.current - 1);
    setUploadResults((prev) => ({ ...prev, [uploadId]: { ...prev[uploadId], assets: prev[uploadId]?.assets?.filter((a) => a.id !== fileId) || [] } }));
  }, []);

  const validatePlacementComplete = useCallback(() => {
    if (!activePlacement) return false;
    const group = IPTV_PLACEMENT_GROUPS[activePlacement];
    if (!Array.isArray(group) || !group.length) return false;
    return group.every((uploadId) => (uploadResults[uploadId]?.assets?.length || 0) > 0);
  }, [activePlacement, uploadResults]);

  const buildContentUpload = useCallback(() => {
    const ContentUpload = {};
    const group = IPTV_PLACEMENT_GROUPS[activePlacement] || [];
    group.forEach((uploadId) => {
      const bucket = uploadResults[uploadId];
      if (!bucket || !bucket.assets?.length) return;
      ContentUpload[uploadId] = { contentGroup: bucket.contentGroup, ledType: bucket.ledType, assets: bucket.assets };
    });
    return ContentUpload;
  }, [activePlacement, uploadResults]);

  const resetForm = useCallback(() => {
    activeUploads.current = 0;
    setUploadResults(() => {
      const results = {};
      Object.keys(UPLOADER_SPECS).forEach((key) => {
        const spec = UPLOADER_SPECS[key];
        results[key] = { contentGroup: spec.contentGroup, ledType: spec.ledType, assets: [] };
      });
      return results;
    });
    setSessionId(generateSessionId()); setSubmissionTimeId(generateCrockfordTimeId());
    setDescription(""); setActivePlacement(null);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!activePlacement) { showError("Please select an IPTV placement.", { duration: 9000 }); return; }
    if (activeUploads.current > 0) { showError("Uploads are still in progress.", { duration: 12000 }); return; }
    if (!validatePlacementComplete()) { showError("Please upload at least one file.", { duration: 12000 }); return; }

    setIsSubmitting(true);
    try {
      const payload = {
        sessionId, submissionParent: context.submissionParent, completedAt: new Date().toISOString(),
        placementId: activePlacement, name: authName || name || context.submitterName, email: authEmail || email,
        description, venue: "MCG", submissionType: "commercial", ContentUpload: buildContentUpload(),
      };
      const res = await fetch(METADATA_WEBHOOK, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error(`Submission failed (${res.status})`);
      
      // IPTV uses unique IDs per file
      const allAssets = [];
      activeUploaderIds.forEach(uploadId => {
        const bucket = uploadResults[uploadId];
        if (bucket?.assets?.length) {
          bucket.assets.forEach(asset => {
            const prefixedName = asset?.prefixedFileName || "";
            const parts = prefixedName.split("_");
            const contentId = parts.length >= 2 ? `${parts[0]}_${parts[1]}` : `${context.submitterShortName || "VF"}_${submissionTimeId}`;
            allAssets.push({ contentId, fileName: asset?.originalFileName || "Uploaded file" });
          });
        }
      });
      
      const placementLabel = IPTV_PLACEMENTS.find(p => p.placementId === activePlacement)?.label || activePlacement;
      setLastUploadInfo({ assets: allAssets, destination: `MCG Commercial IPTV - ${placementLabel}` });
      setShowSuccessModal(true);
      
      resetForm();
    } catch (err) {
      console.error("Submission error:", err);
      showError("Something went wrong. Please try again.", { duration: 6000 });
    } finally { setIsSubmitting(false); }
  };

  const uploadCount = activeUploaderIds.reduce((sum, id) => sum + (uploadResults[id]?.assets?.length || 0), 0);

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <div>
          <h1 style={styles.headerTitle}>Commercial IPTV Content</h1>
          <p style={styles.headerSub}>Upload sponsor/commercial content for MCG IPTV displays</p>
        </div>
        <div style={styles.headerBadges}>
          <span style={styles.venueBadge}>MCG</span>
          <span style={styles.commercialBadge}>Commercial</span>
        </div>
      </header>
      <div style={styles.accentLine} />

      <main style={styles.content}>
        <form onSubmit={handleSubmit}>
          <UserDetailsFields name={name} setName={setName} email={email} setEmail={setEmail} focusedField={focusedField} setFocusedField={setFocusedField} styles={styles} />

          <div style={styles.typeSection}>
            <div style={styles.sectionLabel}>IPTV Placement</div>
            <div style={styles.typeRow}>
              {IPTV_PLACEMENTS.map((p) => <TypeButton key={p.placementId} label={p.label} isActive={activePlacement === p.placementId} onClick={() => setActivePlacement(p.placementId)} disabled={isSubmitting} />)}
            </div>
          </div>

          <div style={styles.uploadSection}>
            <div style={styles.uploadHeader}>
              <h2 style={styles.uploadTitle}>
                <svg style={styles.uploadTitleIcon} width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 3v9M5 7l4-4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 13v2a1 1 0 001 1h10a1 1 0 001-1v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Upload Files
              </h2>
              {uploadCount > 0 && <span style={{ backgroundColor: "#2fd6b5", color: "#000", fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10 }}>{uploadCount} file{uploadCount !== 1 ? "s" : ""} ready</span>}
            </div>
            <div style={styles.uploadBody}>
              {activePlacement && activeUploaderIds.length > 0 ? (
                activeUploaderIds.map((uploadId) => {
                  const spec = UPLOADER_SPECS[uploadId];
                  if (!spec) return null;
                  return <UploaderSection key={uploadId} uploadId={uploadId} spec={spec} context={context} sessionId={sessionId} submissionTimeId={submissionTimeId} onUploadComplete={handleUploadComplete} onUploadStart={handleUploadStart} onUploadCancel={handleUploadCancel} />;
                })
              ) : (
                <div style={styles.placeholderBox}>
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" style={{ marginBottom: 12, color: "#ccc" }}><path d="M20 8v16M12 16l8-8 8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M6 28v4a2 2 0 002 2h24a2 2 0 002-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                  <p>Select an IPTV placement above to start uploading</p>
                </div>
              )}
            </div>
          </div>

          <div style={styles.notesSection}>
            <div style={styles.sectionLabel}>Notes (Optional)</div>
            <textarea value={description} onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESC))} onFocus={() => setFocusedField("notes")} onBlur={() => setFocusedField(null)} placeholder="Add any notes for the operations team..." style={{ ...styles.textarea, ...(focusedField === "notes" ? styles.textareaFocus : {}) }} />
            <div style={styles.charCount}>{description.length}/{MAX_DESC}</div>
          </div>

          <div style={styles.footer}>
            <button type="submit" disabled={isSubmitting || !activePlacement || uploadCount === 0} onMouseEnter={() => setIsSubmitHovered(true)} onMouseLeave={() => setIsSubmitHovered(false)}
              style={{ ...styles.submitBtn, ...(isSubmitHovered && !isSubmitting && activePlacement && uploadCount > 0 ? styles.submitBtnHover : {}), ...((isSubmitting || !activePlacement || uploadCount === 0) ? styles.submitBtnDisabled : {}) }}>
              {isSubmitting ? "Submitting..." : "Submit"}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>
        </form>
      </main>
      {ToastComponent}
      
      {/* Upload Success Modal */}
      <UploadSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        assets={lastUploadInfo?.assets || []}
        destination={lastUploadInfo?.destination}
      />
    </div>
  );
}

export default MCGCommercialIPTVPage;
